import { combineEpics, ofType } from 'redux-observable';
import { mergeMap, switchMap } from 'rxjs/operators';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { EMPTY, forkJoin, of } from 'rxjs';
import { getCompanyID, getUserCountryCode } from 'helper/user';
import { saveCityStorage } from 'helper/storage';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import {
  SELECT_CITY,
  TOGGLE_LOCATION_DRAWER,
  FETCH_FAILED_PAYMENTS_LIST,
  setBlockCashlessPaymentsAction,
  showFailedPaymentsRetryModalAction,
} from 'actions/app-actions';
import {
  fetchServiceTypesActionV2,
  updateOrderShallowAction,
  paxFetchServiceTypesAction,
} from 'actions/booking-actions';
import { GET_USERNAME_INFO } from 'actions/login-actions';
import {
  cat,
  custom,
  event,
  trackPageView,
} from 'common/utils/instrumentation';
import { userInfoSelector } from 'selectors/app-selector';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_HOME_EVENTS,
  SCRIBE_POI_SEARCH_EVENTS,
} from 'applications/scribe/instrumentations/track';

import { doAddressSearch, extractCityFromSearchResults } from './epic-helpers';

const locationDrawerToggledEpic = (action$, state$) =>
  action$.pipe(
    ofType(TOGGLE_LOCATION_DRAWER),
    switchMap(action => {
      const state = state$.value;
      const isOpen = _get(state, 'app.locationDrawer.opening');
      const userInfo = userInfoSelector(state);
      const companyID = getCompanyID(userInfo);
      if (isOpen) {
        trackPageView(event.POI_PV, cat.GE_POI_SEARCH, {
          [custom.COMPANY_ID]: companyID,
        });
        scribeTrackEvent({
          stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
          eventName: SCRIBE_POI_SEARCH_EVENTS.DEFAULT,
          facets: getCommonFacet(userInfo),
        });
      }
      return EMPTY;
    }),
  );

const failedPaymentsListEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_FAILED_PAYMENTS_LIST.SUCCESS),
    switchMap(action => {
      const failedPayments = _get(action, 'data.failedPayments', []);
      if (failedPayments.length === 0) {
        return EMPTY;
      }
      const isCashlessPaymentDisabled = !_isEmpty(failedPayments);
      return of(
        setBlockCashlessPaymentsAction({
          isCashlessPaymentDisabled,
          failedPayments,
        }),
        showFailedPaymentsRetryModalAction(),
      );
    }),
  );

const selectCityEpic = (action$, state$) =>
  action$.pipe(
    ofType(SELECT_CITY),
    mergeMap(action => {
      const cities = _get(state$, 'value.app.cities', []);
      const userInfo = _get(state$, 'value.app.userInfo');

      if (
        _isEmpty(userInfo) ||
        (!isPaxUser() && _isEmpty(userInfo.companies)) ||
        _isEmpty(cities)
      ) {
        return EMPTY;
      }
      const companyID = getCompanyID(userInfo);
      const countryCode = getUserCountryCode(userInfo);
      const cityPOIToSearch = action.payload.poi;
      const cityName = action.payload.name;
      const latlng = `${action.payload.latitude},${action.payload.longitude}`;
      const addressSearch$ = doAddressSearch(
        cityPOIToSearch,
        companyID,
        countryCode,
        latlng,
      );

      return forkJoin(
        addressSearch$,
        of(cityName),
        of(cities),
        of(countryCode),
        of(companyID),
      );
    }),
    switchMap(([{ response }, cityName, cities, countryCode, companyID]) => {
      const userInfo = _get(state$, 'value.app.userInfo');
      const locations = _get(response, 'resp.result', []);
      const cityToStorage = extractCityFromSearchResults(
        locations,
        cities,
        cityName,
        countryCode,
      );
      saveCityStorage(cityToStorage);

      const getUserNameInfoSuccessAction = {
        type: GET_USERNAME_INFO.SUCCESS,
        data: {
          selectedCity: cityToStorage,
        },
      };

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
        eventName: SCRIBE_HOME_EVENTS.CITY_SELECTION,
        facets: getCommonFacet(userInfo),
      });

      return isPaxUser()
        ? of(
            getUserNameInfoSuccessAction,
            paxFetchServiceTypesAction(cityToStorage.cityID),
            updateOrderShallowAction({}),
          )
        : of(
            getUserNameInfoSuccessAction,
            fetchServiceTypesActionV2(cityToStorage.cityID, companyID),
            updateOrderShallowAction({}),
          );
    }),
  );

export default combineEpics(
  locationDrawerToggledEpic,
  selectCityEpic,
  failedPaymentsListEpic,
);
